import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const NAV_LINKS = [
  { label: "首页", to: "/" },
  { label: "影视分类", to: "/category" },
  { label: "关于我们", to: "/about" },
];

export default function SiteHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const solid = !isHome || scrolled || mobileOpen;

  const submitSearch = (event: React.FormEvent) => {
    event.preventDefault();
    const q = keyword.trim();
    if (!q) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
    setKeyword("");
    setMobileOpen(false);
  };

  const linkClass = (to: string) => {
    const active = to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);
    const base = "relative text-sm font-medium transition-colors hover:text-accent-500";
    if (solid) {
      return `${base} ${active ? "text-accent-600" : "text-foreground-700"}`;
    }
    return `${base} ${active ? "text-white" : "text-white/80"}`;
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        solid
          ? "bg-background-50/95 backdrop-blur border-b border-background-200"
          : "bg-gradient-to-b from-foreground-950/70 to-transparent"
      }`}
    >
      <div className="mx-auto flex h-16 md:h-[72px] items-center gap-4 px-4 md:px-6">
        <Link to="/" className="flex items-center gap-2.5 shrink-0">
          <span className="flex items-center justify-center w-9 h-9 rounded-md bg-accent-500 text-background-50">
            <i className="ri-moon-fill text-lg"></i>
          </span>
          <span
            className={`font-heading text-lg font-bold tracking-tight whitespace-nowrap ${
              solid ? "text-foreground-950" : "text-white"
            }`}
          >
            隔壁的女孩
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8 ml-6">
          {NAV_LINKS.map((link) => (
            <Link key={link.to} to={link.to} className={linkClass(link.to)}>
              {link.label}
              {link.to === "/" && location.pathname === "/" && (
                <span className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-accent-500" />
              )}
            </Link>
          ))}
        </nav>

        <form onSubmit={submitSearch} className="hidden md:flex items-center ml-auto">
          <div
            className={`flex items-center gap-2 h-9 pl-3 pr-1 rounded-full border transition-colors ${
              solid ? "border-background-300 bg-background-100" : "border-white/40 bg-white/15"
            }`}
          >
            <i className={`ri-search-line text-sm ${solid ? "text-foreground-400" : "text-white/80"}`}></i>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索影视剧 / 演员"
              className={`w-40 lg:w-52 bg-transparent text-sm outline-none ${
                solid ? "text-foreground-900 placeholder:text-foreground-400" : "text-white placeholder:text-white/60"
              }`}
            />
            <button
              type="submit"
              className="h-7 px-3 rounded-full bg-accent-500 text-background-50 text-xs font-medium whitespace-nowrap hover:bg-accent-600 transition-colors cursor-pointer"
            >
              搜索
            </button>
          </div>
        </form>

        <div className="ml-auto flex md:hidden items-center gap-1">
          <Link
            to="/search"
            className={`flex items-center justify-center w-9 h-9 rounded-md ${
              solid ? "text-foreground-700" : "text-white"
            }`}
          >
            <i className="ri-search-line text-xl"></i>
          </Link>
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            className={`flex items-center justify-center w-9 h-9 rounded-md ${
              solid ? "text-foreground-700" : "text-white"
            }`}
          >
            <i className={`text-xl ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50">
          <div className="px-4 py-5">
            <form onSubmit={submitSearch} className="flex items-center gap-2 mb-5">
              <div className="flex-1 flex items-center gap-2 h-10 px-3 rounded-md border border-background-300 bg-background-100">
                <i className="ri-search-line text-sm text-foreground-400"></i>
                <input
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索影视剧 / 演员"
                  className="flex-1 bg-transparent text-sm outline-none text-foreground-900 placeholder:text-foreground-400"
                />
              </div>
              <button
                type="submit"
                className="h-10 px-4 rounded-md bg-accent-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-accent-600 transition-colors cursor-pointer"
              >
                搜索
              </button>
            </form>
            <div className="flex flex-col">
              {NAV_LINKS.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="flex items-center justify-between py-3.5 border-b border-background-200 text-base text-foreground-800"
                >
                  <span>{link.label}</span>
                  <i className="ri-arrow-right-s-line text-foreground-400"></i>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}